'use client';

import { motion, useMotionValue, useAnimationFrame } from 'framer-motion';
import Image from 'next/image';
import { Calendar, MapPin } from 'lucide-react';
import Link from 'next/link';
import { enCodeEventLink, formatDate } from '@/utils';
import { DateTime } from 'luxon';
import { useRef } from 'react';

export default function EventsCarousel({ events }: any) {
    // Sort events by start date in descending order to get the most recent events
    const sortedEvents = events.sort(
        (a: { startDate: string }, b: { startDate: string }) =>
            new Date(b.startDate).getTime() - new Date(a.startDate).getTime(),
    );

    // Take only the top 5 most recent events
    const recentEvents = sortedEvents.slice(0, 5);

    const x = useMotionValue(0);
    const velocity = -50; // Speed of movement (adjust as needed)
    const isPaused = useRef(false);

    // Calculate the total width of the scrolling content
    const totalWidth = recentEvents.length * 300; // Adjust based on the width of each item

    // Updates the x position on each animation frame
    useAnimationFrame((_, delta) => {
        if (!isPaused.current) {
            const newX = x.get() + (velocity * delta) / 1000;
            if (newX <= -totalWidth) {
                x.set(0); // Reset position to the start of the second set
            } else {
                x.set(newX);
            }
        }
    });

    function extractLastWordAfterComma(htmlString: string): string {
        // Remove HTML tags and non-breaking spaces
        const text = htmlString.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '');

        // Split by commas and trim each part
        const parts = text.split(',').map((part) => part.trim());

        // Get the last part and split by spaces to get the last word
        const lastPart = parts[parts.length - 1];
        const words = lastPart.split(' ');

        // Return the last word
        return words[words.length - 1];
    }

    return (
        <div className="overflow-hidden relative w-[95%] py-6">
            <div
                className="mb-4"
                style={{
                    fontFamily: 'Inter',
                    fontWeight: '700',
                    fontSize: '21.75px',
                    lineHeight: '28.8px',
                    // letterSpacing: '-1.2px',
                }}
            >
                Events
            </div>
            <motion.div
                className="flex gap-6 whitespace-nowrap"
                style={{ x }}
                onHoverStart={() => (isPaused.current = true)}
                onHoverEnd={() => (isPaused.current = false)}
            >
                {[...recentEvents, ...recentEvents].map((event, index) => {
                    const venueText = extractLastWordAfterComma(event.groups.venue.venue || 'Unknown');

                    return (
                        <Link key={index} href={`${enCodeEventLink(event?.eventName, event?.eventId)}`}>
                            <div className="flex items-center gap-4 min-w-[300px] sm:min-w-[400px] overflow-hidden">
                                <Image
                                    src={event?.eventBannerImage || '/placeholder.jpg'}
                                    alt={event?.title || 'Event Image'}
                                    width={1000}
                                    height={1000}
                                    className="w-20 h-16 sm:w-28 sm:h-24 rounded-lg object-cover"
                                />
                                <div className="w-full py-4">
                                    <h3 className="text-[18px] sm:text-md font-semibold">
                                        {event?.eventName?.split(' ').slice(0, 3).join(' ')}
                                        <br />
                                        {event?.eventName?.split(' ').slice(3).join(' ')}
                                    </h3>
                                    <div className="flex items-center">
                                        <Calendar className="mr-1 h-4 w-4 sm:h-4 sm:w-4" />
                                        <p className="text-gray-500 text-[20px] sm:text-sm mr-2">
                                            {formatDate(event?.startDate, false)}
                                        </p>
                                        <MapPin className="mr-1 h-4 w-4 sm:h-4 sm:w-4" />
                                        <p className="text-gray-500 text-[20px] sm:text-sm">{venueText}</p>
                                    </div>

                                    <button className="mt-2 border-2 border-solid border-black text-black px-2 rounded text-sm transition-transform transform hover:scale-105 hover:bg-black hover:text-white">
                                        {DateTime.fromISO(event?.endDate).toFormat('yyyy-MM-dd') <
                                        DateTime.now().toFormat('yyyy-MM-dd')
                                            ? `Watch Now`
                                            : `Register Now`}
                                    </button>
                                </div>
                            </div>
                        </Link>
                    );
                })}
            </motion.div>
        </div>
    );
}
